import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useNotifications } from '../../contexts/NotificationsContextType';

// Icons as React components
import { ReactComponent as AnnuaireIcon } from "../../assets/icons/client-list-blanc.svg";
import { ReactComponent as AccueilIcon } from "../../assets/icons/accueil.svg";
import { ReactComponent as PublicationsIcon } from "../../assets/icons/order-blanc.svg";
import { ReactComponent as CommandesIcon } from "../../assets/icons/commandes.svg";
import { ReactComponent as CatalogueIcon } from "../../assets/icons/catalogue-blanc.svg";
import { ReactComponent as ProfilIcon } from "../../assets/icons/profil-blanc.svg";
import { ReactComponent as DocumentsIcon } from "../../assets/icons/document-blanc.svg";
import { ReactComponent as SupportIcon } from "../../assets/icons/support-blanc.svg";
import { ReactComponent as LogoutIcon } from "../../assets/icons/logout-blanc.svg";
import { ReactComponent as LogoIcon } from "../../assets/logo/logo-centralis-blanc.svg";
import { ReactComponent as LogoTypeIcon } from "../../assets/logo/logotype-centralis.svg";

import "./SideBar.css";

interface SidebarProps {
  unreadCommandes?: number;
  isCollapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

interface LinkItem {
  path: string;
  label: string;
  icon: string;
  notifications?: number;
  show: boolean;
  disabled?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onCollapse }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCommandes = 0, unreadOperations = 0, unreadImportees } = useNotifications() || {};

  const [isUserAcheteur, setIsUserAcheteur] = useState<boolean | null>(null);
  const [sidebarLinks, setSidebarLinks] = useState<LinkItem[]>([]);

  // FRONT-10: État pour la complétion du profil (Prestataires Libres uniquement)
  const [profileIncomplete, setProfileIncomplete] = useState<boolean>(false);

  // Déterminer le rôle et construire les liens dynamiquement
  useEffect(() => {
    const userDataString = localStorage.getItem('user');
    let isAcheteur = false;
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        isAcheteur = ["Acheteur", "Acheteur signataire", "Acheteur signataire admin"].includes(userData.role);
      } catch (error) {
        console.error("Erreur lors du parsing des données utilisateur:", error);
      }
    }

    setIsUserAcheteur(isAcheteur);

    const links: LinkItem[] = [
      {
        path: '/',
        label: 'Accueil',
        icon: 'accueil-icon',
        show: true
      },
      {
        path: '/publications',
        label: 'Publications',
        icon: 'publications-icon',
        show: !!user,
      },
      {
        path: '/commandes',
        label: 'Commandes',
        icon: 'commandes-icon',
        notifications: unreadCommandes,
        show: !!user && !isAcheteur
      },

      {
        path: '/operations',
        label: 'Commandes',
        icon: 'operations-icon',
        notifications: unreadOperations,
        show: !!user && isAcheteur
      },
      // {
      //   path: '/commandes-archive',
      //   label: 'Commandes importées',
      //   icon: 'commandes-icon',
      //   // notifications: unreadCommandes,
      //   show: !!user
      // },
      {
        path: '/annuaire',
        label: 'Annuaire',
        icon: 'annuaire-icon',
        show: true,
      },
      {
        path: '/profil',
        label: 'Profil',
        icon: 'profil-icon',
        show: !!user
      },
      {
        path: '/assistance',
        label: 'Assistance',
        icon: 'support-icon',
        show: true
      },
      {
        path: '/mentions',
        label: 'Mentions',
        icon: 'catalogue-icon',
        show: true
      }
    ];

    setSidebarLinks(links.filter(link => link.show));
  }, [user, unreadCommandes, unreadOperations]);

  // FRONT-10: Charger la complétion du profil pour les Prestataires Libres
  // CORE-19: Ne pas appeler l'API pour les acheteurs
  useEffect(() => {
    const fetchProfileCompletion = async () => {
      // Vérifier si l'utilisateur est connecté
      if (!user) {
        setProfileIncomplete(false);
        return;
      }

      // CORE-19: Déterminer si l'utilisateur est un Prestataire Libre
      const userDataString = localStorage.getItem('user');
      if (!userDataString) {
        setProfileIncomplete(false);
        return;
      }

      try {
        const userData = JSON.parse(userDataString);
        const role = userData.role?.toLowerCase() || '';

        // Ne charger la complétion QUE pour les Prestataires Libres
        const isPrestataireLibre = role.includes('prestataire_libre') || role.includes('prestataire libre');

        if (!isPrestataireLibre) {
          setProfileIncomplete(false);
          return;
        }

        // Appeler l'API de complétion
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/profile/completion`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setProfileIncomplete(data.data?.percent < 100);
        } else {
          setProfileIncomplete(false);
        }
      } catch (error) {
        console.error('Erreur récupération complétion profil:', error);
        setProfileIncomplete(false);
      }
    };

    fetchProfileCompletion();

    // FRONT-10: Re-vérifier la complétion quand le profil est mis à jour
    window.addEventListener('profile-updated', fetchProfileCompletion);
    return () => window.removeEventListener('profile-updated', fetchProfileCompletion);
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getLogo = () => (isCollapsed ? <div className="logo-collapsed"><LogoTypeIcon /></div> : <a href="/"><LogoIcon /></a>);

  return (
    <aside className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
      <div className="first-section">
        <div className="sidebar-header">
          <div className="logo-section">{getLogo()}</div>
        </div>
        <ul className="sidebar-menu">
          {sidebarLinks.map((link) => {
            let IconComponent;
            switch (link.icon) {
              case 'commandes-icon':
              case 'operations-icon':
                IconComponent = CommandesIcon;
                break;
              case 'annuaire-icon':
                IconComponent = AnnuaireIcon;
                break;
              case 'publications-icon':
                IconComponent = PublicationsIcon;
                break;
              case 'accueil-icon':
                IconComponent = AccueilIcon;
                break;
              case 'profil-icon':
                IconComponent = ProfilIcon;
                break;
              case 'support-icon':
                IconComponent = SupportIcon;
                break;
              case 'catalogue-icon':
                IconComponent = CatalogueIcon;
                break;
              default:
                IconComponent = DocumentsIcon;
            }

            return (
              <li key={link.label}>
                <NavLink
                  to={link.disabled ? "#" : link.path} // si désactivé, pas de navigation
                  className={({ isActive }) => `sidebar-link ${isActive && !link.disabled ? 'active' : ''} ${link.disabled ? 'disabled' : ''}`}
                  onClick={(e) => link.disabled && e.preventDefault()} // bloquer le clic
                >
                  <IconComponent className="icon" />
                  {!isCollapsed && <span>{link.label}</span>}
                  {(link.path === '/commandes' || link.path === '/operations') && (
                    typeof link.notifications === "number" && link.notifications > 0 && (
                      <span className="badge">
                        {link.notifications > 999
                          ? `+${Math.floor(link.notifications / 1000)}k`
                          : link.notifications > 99
                            ? "+99"
                            : link.notifications}
                      </span>
                    )
                  )}
                  {/* FRONT-10: Badge profil incomplet pour Prestataires Libres */}
                  {link.path === '/profil' && profileIncomplete && (
                    <span className="badge badge-warning">!</span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
        <ul className="sidebar-footer">
          <li>
            {user ? (
              <button onClick={handleLogout} className="sidebar-link logout-btn">
                <LogoutIcon className="icon" />
                {!isCollapsed && <span>Déconnexion</span>}
              </button>
            ) : (
              <NavLink to='/login' className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              >
                <ProfilIcon className="icon" />
                Connexion
              </NavLink>
            )}
          </li>
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;